import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { OVERVIEW_NS } from "@/components/overview/i18n";
import { OverviewError } from "@/components/overview/overview-error";
import { ProfileSkeleton } from "@/components/overview/overview-skeleton";
import { PlusCard } from "@/components/overview/plus-card";
import { PointsBadgeCard } from "@/components/overview/points-badge-card";
import { PointsCard } from "@/components/overview/points-card";
import { ProfileCard } from "@/components/overview/profile-card";
import { ReferralsCard } from "@/components/overview/referrals-card";
import { ServicesCard } from "@/components/overview/services-card";
import { SetupCard } from "@/components/overview/setup-card";
import { computeSetupSteps, nextSetupStep } from "@/components/overview/setup-steps";
import { TryServicesCard } from "@/components/overview/try-services-card";
import { EarnTasksCard } from "@/components/points/earn-tasks-card";
import { useStickyColumn } from "@/lib/sticky-column";
import { api } from "@/lib/trpc";

export const Route = createFileRoute("/")({
	component: OverviewPage,
});

/**
 * Раздел «Главная» (макет 2a): колонка 640 и правая колонка 300, зазор 4px.
 *
 * Каждая карточка сама ходит за своими данными и сама показывает загрузку,
 * пустоту и офлайн. Общего спиннера на весь экран нет намеренно: баланс
 * и сервисы приходят разными запросами, и один упавший запрос не должен
 * прятать остальные карточки.
 */
function OverviewPage() {
	const { t } = useTranslation(OVERVIEW_NS);

	const asideRef = useStickyColumn<HTMLElement>();

	const profile = api.profile.get.useQuery();
	const legal = api.legal.pendingAcceptance.useQuery({});

	return (
		// НА МОБИЛЬНОМ КОЛОНОК НЕТ. Обе обёртки становятся `display: contents`,
		// то есть карточки превращаются в детей одного столбца, и порядок им
		// задаёт `order`: профиль → IEC+ → Баллы → сервисы → всё остальное
		// (решение владельца 22.08.2026: подписка и баланс должны встречать
		// человека, а не ждать в конце страницы). На `lg` обёртки снова
		// колонки, а `order` сбрасывается.
		<div className="flex flex-col gap-1 lg:flex-row lg:items-start">
			{/* Ширина основной колонки — гибкая, а не 640px фиксом. На широком
			    экране контейнер всё равно упирается в max-w-[1200px] и колонка
			    выходит теми же ~640px, зато между 1024 и 1194px страница больше не
			    вылезает вбок: 220 (боковое меню) + 640 + 300 (правая колонка) +
			    отступы = 1194, то есть при `shrink-0` на обеих колонках у флекса
			    просто не было способа поместиться. */}
			<div className="contents lg:flex lg:min-w-0 lg:flex-1 lg:flex-col lg:gap-1">
				<h1 className="sr-only">{t("title")}</h1>

				{profile.isPending ? (
					<div className="order-1 lg:order-none">
						<ProfileSkeleton />
					</div>
				) : null}
				{profile.isError ? (
					<div className="order-1 lg:order-none">
						<OverviewError onRetry={() => void profile.refetch()} />
					</div>
				) : null}
				{profile.data ? (
					<>
						<div className="order-1 lg:order-none">
							<ProfileCard
								firstName={profile.data.firstName ?? ""}
								displayName={[profile.data.firstName, profile.data.lastName]
									.filter(Boolean)
									.join(" ")}
								email={profile.data.email}
								avatarUrl={profile.data.avatarUrl}
								createdAt={profile.data.createdAt}
							/>
						</div>
						<div className="order-4 lg:order-none">
							{nextSetupStep(
								computeSetupSteps({
									avatarUrl: profile.data.avatarUrl,
									hasCustomAvatar: profile.data.hasCustomAvatar,
									nickname: profile.data.nickname,
									ageConfirmedAt: profile.data.ageConfirmedAt,
									documentsPending: legal.data?.items.length ?? 0,
								}),
							) ? (
								<SetupCard
									avatarUrl={profile.data.avatarUrl}
									hasCustomAvatar={profile.data.hasCustomAvatar}
									nickname={profile.data.nickname}
									ageConfirmedAt={profile.data.ageConfirmedAt}
									documentsPending={legal.data?.items.length ?? 0}
									version={profile.data.version}
								/>
							) : (
								<TryServicesCard />
							)}
						</div>
					</>
				) : null}

				<div className="order-5 lg:order-none">
					<PointsCard />
				</div>
				{/* Задачи «как получить ещё Баллы» — сразу под балансом: сумма и
				    способ её увеличить читаются вместе. Карточка исчезает сама,
				    когда открытых задач не осталось. */}
				<div className="order-5 lg:order-none">
					<EarnTasksCard place="overview" />
				</div>
				<div className="order-6 lg:order-none">
					<ServicesCard />
				</div>
			</div>

			<aside
				ref={asideRef}
				className="contents lg:sticky lg:flex lg:w-[300px] lg:shrink-0 lg:flex-col lg:gap-1"
			>
				{/* На десктопе баланс первый в колонке: Баллы — то, ради чего
				    возвращаются. На мобильном перед ним встаёт IEC+ (order-2). */}
				<div className="order-3 lg:order-none">
					<PointsBadgeCard />
				</div>
				<div className="order-2 lg:order-none">
					<PlusCard />
				</div>
				<div className="order-7 lg:order-none">
					<ReferralsCard />
				</div>
			</aside>
		</div>
	);
}
